import * as THREE from 'three/webgpu';
import type { Viewer } from '../../../viewer/Viewer';
import {
  MoveComponentInputMode,
  MoveAlongEntitySelectionPhase,
} from '../MoveComponentTypes';
import type {
  MoveAlongEntityConstraintKind,
  MoveAlongEntityReferenceType,
} from '../MoveComponentTypes';
import { MoveAlongEntityReferenceResolver } from './MoveAlongEntityReferenceResolver';
import type { MoveAlongEntityResolvedReference } from './MoveAlongEntityReferenceResolver';
import { MoveService } from './MoveService';
import type { AssemblyTransformHistoryRecord } from '../../component-instance';

/**
 * 装配体移动子控制器。
 */
export class MoveController {
  private readonly viewer: Viewer;
  private readonly moveService: MoveService;
  private readonly alongEntityReferenceResolver: MoveAlongEntityReferenceResolver;
  private moveInputMode: MoveComponentInputMode;
  /** 是否启用碰撞检测。 */
  private collisionEnabled = false;
  /** 当前沿实体参考运行时快照。 */
  private alongEntityReference: MoveAlongEntityResolvedReference | null = null;

  /**
   * 创建移动子控制器。
   *
   * @param viewer 场景视图实例。
   * @param moveService 移动服务。
   * @param defaultInputMode 默认输入模式。
   */
  public constructor(
    viewer: Viewer,
    moveService: MoveService,
    defaultInputMode: MoveComponentInputMode,
  ) {
    this.viewer = viewer;
    this.moveService = moveService;
    this.alongEntityReferenceResolver = new MoveAlongEntityReferenceResolver(viewer);
    this.moveInputMode = defaultInputMode;
  }

  /**
   * 获取当前移动输入模式。
   *
   * @returns 当前输入模式。
   */
  public getMoveInputMode(): MoveComponentInputMode {
    return this.moveInputMode;
  }

  /**
   * 设置移动输入模式。
   *
   * @param mode 目标模式。
   */
  public setMoveInputMode(mode: MoveComponentInputMode): void {
    this.moveInputMode = mode;
  }

  /**
   * 判断当前是否已选中沿实体参考对象。
   *
   * @returns 是否存在参考对象。
   */
  public hasAlongEntityReference(): boolean {
    return this.alongEntityReference !== null;
  }

  /**
   * 读取当前沿实体参考运行时快照。
   *
   * @returns 运行时快照；未选择时返回 `null`。
   */
  public getAlongEntityReference(): MoveAlongEntityResolvedReference | null {
    if (!this.alongEntityReference) {
      return null;
    }
    return {
      ...this.alongEntityReference,
      anchorWorld: this.alongEntityReference.anchorWorld.clone(),
      directionWorld: this.alongEntityReference.directionWorld?.clone() ?? null,
      planeNormalWorld: this.alongEntityReference.planeNormalWorld?.clone() ?? null,
    };
  }

  /**
   * 判断对象是否可作为沿实体参考对象。
   *
   * @param object 待判断对象。
   * @returns 是否可作为参考对象。
   */
  public canUseAsAlongEntityReference(object: THREE.Object3D | null | undefined): boolean {
    return this.alongEntityReferenceResolver.canUseAsReference(object ?? null);
  }

  /**
   * 写入当前沿实体参考对象。
   *
   * @param object 参考对象。
   * @returns 是否写入成功。
   */
  public setAlongEntityReference(object: THREE.Object3D | null): boolean {
    const resolvedReference = this.alongEntityReferenceResolver.resolve(object);
    if (!resolvedReference) {
      return false;
    }
    this.alongEntityReference = resolvedReference;
    return true;
  }

  /**
   * 清空当前沿实体参考对象。
   */
  public clearAlongEntityReference(): void {
    this.alongEntityReference = null;
  }

  /**
   * 读取当前沿实体参考对象 ID。
   *
   * @returns 对象 ID；未设置时返回 `null`。
   */
  public getAlongEntityReferenceObjectId(): string | null {
    return this.alongEntityReference?.objectId ?? null;
  }

  /**
   * 读取当前沿实体参考对象显示名称。
   *
   * @returns 显示名称；未设置时返回 `null`。
   */
  public getAlongEntityReferenceLabel(): string | null {
    return this.alongEntityReference?.label ?? null;
  }

  /**
   * 读取当前沿实体参考对象类型。
   *
   * @returns 对象类型；未设置时返回 `null`。
   */
  public getAlongEntityReferenceType(): MoveAlongEntityReferenceType | null {
    return this.alongEntityReference?.referenceType ?? null;
  }

  /**
   * 读取当前沿实体约束类型。
   *
   * @returns 约束类型；未设置时返回 `null`。
   */
  public getAlongEntityConstraintKind(): MoveAlongEntityConstraintKind | null {
    return this.alongEntityReference?.constraintKind ?? null;
  }

  /**
   * 读取当前沿实体选择阶段。
   *
   * @returns 选择阶段。
   */
  public getAlongEntitySelectionPhase(): MoveAlongEntitySelectionPhase {
    return this.alongEntityReference
      ? MoveAlongEntitySelectionPhase.AwaitingComponent
      : MoveAlongEntitySelectionPhase.AwaitingReference;
  }

  /**
   * 设置是否启用碰撞检测。
   *
   * @param enabled 是否启用。
   */
  public setCollisionEnabled(enabled: boolean): void {
    this.collisionEnabled = enabled;
  }

  /**
   * 读取当前碰撞检测开关状态。
   *
   * @returns 是否启用碰撞检测。
   */
  public isCollisionEnabled(): boolean {
    return this.collisionEnabled;
  }

  /**
   * 预览 Delta XYZ 移动。
   *
   * @param delta 三轴增量。
   */
  public previewDeltaMove(delta: readonly [number, number, number]): void {
    //------ 沿装配体坐标移动收口 开始------
    // AlongAssemblyAxis 明确按装配体本地坐标系（X/Y/Z）解释输入增量。
    if (this.moveInputMode === MoveComponentInputMode.AlongAssemblyAxis) {
      this.moveService.previewMoveByAssemblyLocalDelta(delta);
      return;
    }
    // 其它模式暂复用原有增量位移求解。
    this.moveService.previewMoveByDelta(delta);
    //------ 沿装配体坐标移动收口 结束------
  }

  /**
   * 预览由 Delta XYZ 执行的装配体坐标移动。
   *
   * @param delta 装配体坐标系下的三轴增量。
   */
  public previewDeltaMoveByAssemblyCoordinates(delta: readonly [number, number, number]): void {
    this.moveService.previewMoveByAssemblyLocalDelta(delta);
  }

  /**
   * 预览移动到目标位置。
   *
   * @param position 目标坐标。
   */
  public previewMoveToPosition(position: readonly [number, number, number]): void {
    this.moveService.previewMoveToPosition(position);
  }

  /**
   * 读取当前选中目标的装配体世界坐标。
   *
   * @returns 当前选中目标的世界坐标。
   */
  public readSelectedTargetWorldPosition(): readonly [number, number, number] {
    return this.moveService.readSelectedTargetWorldPosition();
  }

  /**
   * 提交移动预览。
   */
  public commitPreview(): AssemblyTransformHistoryRecord | null {
    const record = this.moveService.commitPreview();
    void this.viewer;

    return record;
  }

  /**
   * 回滚移动预览。
   */
  public cancelPreview(): void {
    this.moveService.cancelPreview();
    void this.viewer;
  }

  /**
   * 释放移动控制器资源。
   */
  public dispose(): void {
    this.clearAlongEntityReference();
    this.moveService.dispose();
    void this.viewer;
  }
}
